import { useField, useFormikContext } from 'formik';

import type { PortMessage } from '../../../types/PortMessage';
import { PaginatedAsyncMultiComboBox } from '../../cdl/ComboBox/ComboBox';
import { getPorts } from '../api/clients/port.api';
import { queries } from '../api/queryKeys/queries';
import { formatPort } from '../helpers/formatPort.helper';

interface PortOption {
    label: string;
    value: string;
    port: PortMessage;
}

const toPortOption = (port: PortMessage): PortOption => ({
    label: formatPort({ port }),
    value: port.id,
    port,
});

interface FormikMultiPortSelectProps {
    name: string;
    label?: string;
    placeholder?: string;
    caption?: string;
    markAsRequired?: boolean;
}

export const FormikMultiPortSelect = ({ name, caption: captionProp, ...props }: FormikMultiPortSelectProps) => {
    const [field, meta, helpers] = useField<string[]>(name);
    const { validateOnChange } = useFormikContext();
    const caption = meta.touched && meta.error ? meta.error : captionProp;

    const handleChange = (portIds: string[]) => {
        helpers.setValue(portIds);

        if (!validateOnChange && meta.error) {
            helpers.setError(undefined);
        }
    };

    const loadSelectedOptions = async (ids: string[]): Promise<PortOption[]> => {
        if (!ids.length) {
            return [];
        }

        const response = await getPorts({ ids });
        const portsById = new Map(response.content.map((port) => [port.id, port]));

        return ids.flatMap((id) => {
            const port = portsById.get(id);
            return port ? [toPortOption(port)] : [];
        });
    };

    return (
        <PaginatedAsyncMultiComboBox<PortOption>
            {...props}
            value={field.value}
            onChange={handleChange}
            negative={meta.touched && !!meta.error}
            caption={caption}
            onBlur={() => helpers.setTouched(true)}
            optionsQueryKey={(search) => queries.ports.paginatedComboBoxOptions({ searchQuery: search }).queryKey}
            optionsQueryFn={async ({ search, page }) => {
                const response = await getPorts({ searchQuery: search, page });

                return {
                    ...response,
                    content: response.content.map(toPortOption),
                };
            }}
            selectedOptionsQueryKey={(ids) => queries.ports.comboBoxOptions({ ids }).queryKey}
            selectedOptionsQueryFn={loadSelectedOptions}
        />
    );
};
